import axios from 'axios';
import { Post, PostsResponse, CreatePostResponse } from './communityApi';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface CreatePostData {
  title: string;
  content?: string;
  type: 'text' | 'image' | 'video';
  communityId?: string; // Optional community tag
  tags?: string; // Comma-separated tags
  media?: File | File[]; // Single or multiple media files
}

export interface UpdatePostData {
  title?: string;
  content?: string;
  tags?: string; // Comma-separated tags
}

// New interfaces for vote and comment responses
export interface VoteResponse {
  success: boolean;
  message: string;
  post: {
    id: string;
    upvotes: number;
    downvotes: number;
    score: number;
    userVote: 'up' | 'down' | null;
  };
}

export interface UserVoteResponse {
  success: boolean;
  userVote: 'up' | 'down' | null;
}

export interface PostVotesResponse {
  success: boolean;
  votes: {
    upvotes: number;
    downvotes: number;
    score: number;
    upvoters: string[];
    downvoters: string[];
  };
}

export interface Comment {
  userVote: null;
  id: string;
  content: string;
  author: {
    id: string;
    username: string;
    profilePicture?: string;
  };
  upvotesCount: number;
  downvotesCount: number;
  repliesCount: number;
  createdAt: string;
  updatedAt: string;
  parentComment?: string;
}

export interface CommentsResponse {
  success: boolean;
  comments: Comment[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface CreateCommentData {
  content: string;
  parentCommentId?: string;
}

// Add ReportResponse interface
export interface ReportResponse {
  error: string;
  success: boolean;
  message: string;
  report: {
    id: string;
    status: string;
    timestamp: string;
  };
}

export interface ReportPostData {
  reportType: string;
  customReason?: string;
  description?: string;
  postTitle: string;
  postAuthorId: string;
  postAuthorUsername: string;
}

export const postApi = {
  // Get all posts (feed) - automatically uses authenticated endpoint when logged in
  getPosts: async (params?: {
    type?: string;
    community?: string;
    author?: string;
    search?: string;
    sortBy?: string;
    page?: number;
    limit?: number;
  }): Promise<PostsResponse> => {
    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        // User is authenticated, try the authenticated endpoint first
        try {
          const response = await api.get('/api/posts/user/feed', { params });
          console.log('📥 Using authenticated posts endpoint');
          return response.data;
        } catch (authError: any) {
          console.log('❌ Authenticated endpoint failed, falling back to public:', authError.message);
          // Fall back to public endpoint
          const response = await api.get('/api/posts', { params });
          return response.data;
        }
      } else {
        // User is not authenticated, use public endpoint
        const response = await api.get('/api/posts', { params });
        console.log('📥 Using public posts endpoint');
        return response.data;
      }
    } catch (error: any) {
      console.error('Get posts error:', error);
      return {
        success: false,
        posts: [],
        pagination: {
          total: 0,
          page: 1,
          limit: 20,
          pages: 0
        },
        error: error.message || 'Failed to get posts'
      };
    }
  },

  // Get single post - automatically uses authenticated endpoint when logged in
  getPost: async (postId: string): Promise<{
    error: string;
    success: boolean;
    post: Post;
  }> => {
    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        // User is authenticated, try the authenticated endpoint first
        try {
          const response = await api.get(`/api/posts/user/${postId}`);
          console.log('📥 Using authenticated single post endpoint');
          return response.data;
        } catch (authError: any) {
          console.log('❌ Authenticated endpoint failed, falling back to public:', authError.message);
          // Fall back to public endpoint
          const response = await api.get(`/api/posts/${postId}`);
          return response.data;
        }
      } else {
        // User is not authenticated, use public endpoint
        const response = await api.get(`/api/posts/${postId}`);
        console.log('📥 Using public single post endpoint');
        return response.data;
      }
    } catch (error: any) {
      console.error('Get post error:', error);
      return {
        success: false,
        post: null as any,
        error: error.message || 'Failed to get post'
      };
    }
  },

  // Get all posts with user's vote status (authenticated only - explicit)
  getPostsWithUserVotes: async (params?: {
    type?: string;
    community?: string;
    author?: string;
    search?: string;
    sortBy?: string;
    page?: number;
    limit?: number;
  }): Promise<PostsResponse> => {
    const response = await api.get('/api/posts/user/feed', { params });
    return response.data;
  },

  // Get single post with user's vote status (authenticated only - explicit)
  getPostWithUserVote: async (postId: string): Promise<{
    error: string;
    success: boolean;
    post: Post;
  }> => {
    const response = await api.get(`/api/posts/user/${postId}`);
    return response.data;
  },

  // Create independent post (supports multiple images)
  createPost: async (data: CreatePostData): Promise<CreatePostResponse> => {
    const formData = new FormData();
    
    // Add text fields
    formData.append('title', data.title);
    if (data.content) formData.append('content', data.content);
    formData.append('type', data.type);
    if (data.communityId) formData.append('communityId', data.communityId);
    if (data.tags) formData.append('tags', data.tags);
    
    // Add media file(s)
    if (data.media) {
      if (Array.isArray(data.media)) {
        // Multiple files (for images)
        data.media.forEach((file, index) => {
          formData.append('media', file);
        });
      } else {
        // Single file (for videos or single image)
        formData.append('media', data.media);
      }
    }
    
    const response = await api.post('/api/posts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    return response.data;
  },

  // Update post
  updatePost: async (
    postId: string,
    data: UpdatePostData
  ): Promise<{
    error: string; 
    success: boolean; 
    message: string;
    post: {
      id: string;
      title: string;
      content: string;
      tags: string[];
      updatedAt: string;
    }
  }> => {
    const response = await api.put(`/api/posts/${postId}`, data);
    return response.data;
  },

  // Delete post
  deletePost: async (postId: string): Promise<{
    error: string; 
    success: boolean; 
    message: string 
  }> => {
    const response = await api.delete(`/api/posts/${postId}`);
    return response.data;
  },

  // ============ VOTE ENDPOINTS ============
  
  // Vote on post (moved to separate endpoint)
  votePost: async (
    postId: string,
    vote: 'up' | 'down' | 'remove'
  ): Promise<VoteResponse> => {
    const response = await api.post(`/api/votes/${postId}`, { vote });
    return response.data;
  },

  // Get user's vote status for a post
  getUserVoteStatus: async (postId: string): Promise<UserVoteResponse> => {
    const response = await api.get(`/api/votes/${postId}/user`);
    return response.data;
  },

  // Get all votes for a post
  getPostVotes: async (postId: string): Promise<PostVotesResponse> => {
    const response = await api.get(`/api/votes/${postId}`);
    return response.data;
  },

  // ============ COMMENT ENDPOINTS ============
  
  // Create comment
  createComment: async (
    postId: string, 
    data: CreateCommentData
  ): Promise<{
    success: boolean;
    message: string;
    comment: Comment;
  }> => {
    const response = await api.post(`/api/comments/${postId}`, data);
    return response.data;
  },

  // Get comments for a post
  getPostComments: async (
    postId: string,
    params?: {
      parentCommentId?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<CommentsResponse> => {
    const response = await api.get(`/api/comments/${postId}`, { params });
    return response.data;
  },

  // Update comment
  updateComment: async (
    commentId: string,
    content: string
  ): Promise<{
    success: boolean;
    message: string;
    comment: Comment;
  }> => {
    const response = await api.put(`/api/comments/${commentId}`, { content });
    return response.data;
  },

  // Delete comment
  deleteComment: async (commentId: string): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(`/api/comments/${commentId}`);
    return response.data;
  },

  // ============ REPORT ENDPOINTS ============
  
  // Report a post
  reportPost: async (
    postId: string,
    reportData: ReportPostData
  ): Promise<ReportResponse> => {
    const response = await api.post('/api/reports', {
      postId,
      ...reportData
    });
    return response.data;
  },

  // ============ BATCH OPERATIONS ============
  
  // Get posts with user vote status (optimized for feed)
  getPostsWithVotes: async (params?: {
    type?: string;
    community?: string;
    author?: string;
    search?: string;
    sortBy?: string;
    page?: number;
    limit?: number;
  }): Promise<PostsResponse> => {
    // Use the main getPosts function which automatically handles authentication
    const postsResponse = await postApi.getPosts(params);
    
    // If user is not authenticated, the backend already returns userVote as null
    // If user is authenticated, the backend returns userVote from the authenticated endpoint
    // So no need for additional API calls
    
    return postsResponse;
  },

  // Get post with full details (post + votes + comments count)
  getPostWithDetails: async (postId: string): Promise<{
    success: boolean;
    post: Post;
    comments?: Comment[];
  }> => {
    try {
      // Get post details with automatic authentication handling
      const postResponse = await postApi.getPost(postId);
      
      if (!postResponse.success) {
        return postResponse;
      }
      
      // Get comments in parallel
      const commentsPromise = postApi.getPostComments(postId, { limit: 5 }).catch(() => null);
      
      const [commentsResponse] = await Promise.all([commentsPromise]);
      
      return {
        ...postResponse,
        comments: commentsResponse?.comments || []
      };
    } catch (error) {
      console.error('Failed to get post with details:', error);
      return {
        success: false,
        post: null as any,
        // error: 'Failed to load post details'
      };
    }
  }
};

export default postApi;